import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowRight, LogIn } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAdminAuth } from "./AdminAuthContext";

const inputCls =
  "bg-slate-900 border border-white/10 text-slate-100 placeholder:text-slate-600 rounded-none h-12 focus-visible:ring-1 focus-visible:ring-cyan-400 focus-visible:border-cyan-400 focus-visible:ring-offset-0";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter the admin password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(password);
      toast.success("Welcome back, admin.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid password";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center px-6 bg-slate-950 bg-grid"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 border border-cyan-400/40 bg-cyan-400/10 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-cyan-300" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-cyan-400 font-mono">
              Admin
            </div>
            <div className="text-lg font-medium text-slate-50 tracking-tight">
              GenX Technologies
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-slate-900 p-7 md:p-9 tracing-card">
          <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-slate-50 mb-2">
            Sign in to the dashboard
          </h1>
          <p className="text-sm text-slate-400 mb-7">
            Enter your admin password to view inquiries.
          </p>

          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono"
              >
                Admin Password
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                autoFocus
                autoComplete="current-password"
                className={inputCls}
              />
            </div>

            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-none font-semibold h-12 btn-glow disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" /> Sign In
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-xs text-slate-500 text-center">
          Forgot the password? Contact the site owner to reset it.
        </p>
      </div>
    </main>
  );
}
